import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Star, GraduationCap,
  UserCircle, BookOpenCheck, Lightbulb, Rocket, PartyPopper, MapPin,
} from "lucide-react";

const REGIONS = [
  "مكة المكرمة","المدينة المنورة","القصيم","الرياض","المنطقة الشرقية",
  "تبوك","نجران","حائل","الحدود الشمالية","الجوف","عسير","جازان","الباحة","أخرى",
];
const AGES = ["أقل من 20", "20–25", "25–30", "أكبر من 30"];
const UNIVERSITIES = [
  "جامعة الملك عبدالعزيز","جامعة الملك سعود","جامعة الملك فهد للبترول والمعادن",
  "جامعة الملك عبدالله للعلوم والتقنية","جامعة أم القرى","جامعة الإمام عبدالرحمن بن فيصل",
  "جامعة الفيصل","أخرى",
];
const ACADEMIC_FIELDS = ["إداري","اجتماعي","صحي","تقني","هندسي","إعلامي","شرعي/قانوني","أخرى"];
const ACADEMIC_LEVELS = ["السنة الأولى","السنة الثانية","السنة الثالثة","السنة الرابعة","خريج حديث"];
const EXP_TYPES = ["تدريب تعاوني","تدريب ميداني","تطوع","عمل جزئي"];
const NO_EXP_REASONS = ["عدم توفر فرص مناسبة","ضيق الوقت","ضعف المعلومات","أخرى"];
const INTEREST_NGO = ["مرتفع","متوسط","ضعيف","لا يوجد"];
const GOALS = [
  "اكتساب خبرة عملية","تطوير مهارات مهنية","بناء شبكة علاقات",
  "تحسين فرص التوظيف","خدمة المجتمع","أخرى",
];
const DURATIONS = ["4–6 أسابيع","8–12 أسبوعاً","3–6 أشهر","أخرى"];
const MODES = ["حضوري","عن بُعد","هجين"];
const SKILLS = [
  { k: "skill_writing", label: "التواصل الكتابي وإعداد التقارير" },
  { k: "skill_presentation", label: "مهارات العرض والتقديم" },
  { k: "skill_teamwork", label: "العمل الجماعي" },
  { k: "skill_time_management", label: "إدارة الوقت" },
  { k: "skill_innovation", label: "الابتكار في حل المشكلات" },
  { k: "skill_digital", label: "استخدام الحاسب والأدوات الرقمية" },
  { k: "skill_excel", label: "استخدام Excel" },
  { k: "skill_research", label: "البحث وجمع المعلومات" },
  { k: "skill_analytical", label: "التفكير التحليلي" },
  { k: "skill_project_mgmt", label: "التخطيط وإدارة المشاريع" },
  { k: "skill_leadership", label: "القيادة" },
  { k: "skill_flexibility", label: "المرونة والتكيف" },
  { k: "skill_quality", label: "الجودة في إنجاز المهام" },
];

const STEP_META = [
  { title: "أهلاً بك في عماد", subtitle: "نبدأ رحلتك بأسئلة قصيرة تُخصّص لك التجربة", icon: Sparkles, hue: "from-teal-700 to-teal-900" },
  { title: "لنتعرّف عليك", subtitle: "معلومات أساسية سريعة", icon: UserCircle, hue: "from-sky-700 to-teal-800" },
  { title: "رحلتك الأكاديمية", subtitle: "أين أنت الآن؟", icon: GraduationCap, hue: "from-emerald-700 to-teal-800" },
  { title: "خبراتك السابقة", subtitle: "أخبرنا عن تجاربك", icon: BookOpenCheck, hue: "from-amber-700 to-orange-800" },
  { title: "طموحاتك", subtitle: "ماذا تنتظر من التدريب؟", icon: Lightbulb, hue: "from-purple-700 to-indigo-800" },
  { title: "مهاراتك اليوم", subtitle: "قيّم نفسك بصدق — لا يوجد إجابة خاطئة", icon: Star, hue: "from-rose-700 to-red-800" },
  { title: "انطلق!", subtitle: "شكراً — نحن جاهزون لك", icon: PartyPopper, hue: "from-teal-600 to-emerald-700" },
];

export default function OnboardingSurvey() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(() => {
    const cached = localStorage.getItem("emad_survey_draft");
    return cached ? JSON.parse(cached) : {
      full_name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
    };
  });
  useEffect(() => { localStorage.setItem("emad_survey_draft", JSON.stringify(values)); }, [values]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const validators = useMemo(() => [
    () => true, // step 0: intro
    () => values.full_name && values.gender && values.nationality && values.region && values.city && values.age_range && values.phone && values.email, // step 1
    () => values.university && (values.university !== "أخرى" || values.university_other) && values.exact_specialization && values.academic_field && values.academic_level, // step 2
    () => values.has_experience === "نعم" ? (values.experience_type && values.experience_rating) : values.has_experience === "لا" ? (values.no_experience_reason && (values.no_experience_reason !== "أخرى" || values.no_experience_reason_other)) : false, // step 3
    () => values.interest_ngos && values.commitment_level && (values.goals?.length > 0) && values.preferred_duration && values.preferred_mode, // step 4
    () => SKILLS.every((s) => values[s.k] >= 1 && values[s.k] <= 5), // step 5
    () => true, // step 6 done
  ], [values]);

  const canNext = validators[step]?.();

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/student/survey", values);
      trackEvent("onboarding_completed", { role: "student" });
      localStorage.removeItem("emad_survey_draft");
      toast.success("تم حفظ الاستبيان — أهلاً بك في عماد");
      if (refresh) await refresh();
      setStep(STEP_META.length - 1);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const meta = STEP_META[step];
  const Icon = meta.icon;
  const progress = Math.round((step / (STEP_META.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-cream-50" dir="rtl" data-testid="onboarding-page">
      {/* Top bar */}
      <div className={`bg-gradient-to-l ${meta.hue} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo variant="cream" size={80} />
          <div className="text-xs font-semibold opacity-90 arabic-numerals">
            الخطوة {step + 1} من {STEP_META.length}
          </div>
        </div>
        <div className="h-1.5 bg-black/20">
          <div className="h-full bg-white/90 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start gap-4">
          <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${meta.hue} text-white grid place-items-center shrink-0`}>
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">رحلة عماد</div>
            <h1 className="mt-1 text-2xl font-heading font-black text-slate-900">{meta.title}</h1>
            <p className="mt-1 text-slate-600">{meta.subtitle}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-cream-200 card-shadow p-4 md:p-8" data-testid={`survey-step-${step}`}>
          {step === 0 && <StepIntro user={user} onStart={() => setStep(1)} />}
          {step === 1 && <StepPersonal values={values} set={set} />}
          {step === 2 && <StepAcademic values={values} set={set} />}
          {step === 3 && <StepExperience values={values} set={set} />}
          {step === 4 && <StepExpectations values={values} set={set} />}
          {step === 5 && <StepSkills values={values} set={set} />}
          {step === 6 && <StepDone user={user} />}
        </div>

        {step > 0 && step < 6 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold"
              data-testid="survey-prev"
            >
              <ArrowRight className="h-4 w-4" /> السابق
            </button>
            {step < 5 ? (
              <button
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-50 text-sm font-semibold"
                data-testid="survey-next"
              >
                التالي <ArrowLeft className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!canNext || saving}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-60 text-sm font-semibold"
                data-testid="survey-submit"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                إرسال والانطلاق
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -------- Steps --------
function StepIntro({ user, onStart }) {
  return (
    <div className="space-y-5">
      <p className="text-lg text-slate-800 leading-relaxed">
        أهلاً بك عزيزي <strong className="text-teal-700">{user?.name?.split(" ")[0] || "صديقي"}</strong> 👋<br/>
        قبل أن نعتمدك بشكل نهائي في المنصّة، نودّ أن نتعرّف عليك بشكل أعمق كي نُخصّص لك فرص التدريب التعاوني التي تناسب رحلتك. لن يستغرق منك الأمر أكثر من <strong>4 دقائق</strong>.
      </p>
      <ul className="space-y-2 text-sm text-slate-600">
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> لا يوجد إجابات صحيحة أو خاطئة — كن صادقاً</li>
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> يمكنك الرجوع لأي خطوة سابقة في أي وقت</li>
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> إجاباتك تُحفظ تلقائياً حتى لو أغلقت الصفحة</li>
      </ul>
      <div className="pt-4">
        <button onClick={onStart} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-teal-700 text-white text-base font-semibold hover:bg-teal-600" data-testid="survey-start">
          هيا نبدأ <ArrowLeft className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function StepPersonal({ values, set }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Text label="الاسم الثلاثي" value={values.full_name} onChange={(v) => set("full_name", v)} testid="q-fullname" />
      <RadioGroup label="الجنس" options={["ذكر","أنثى"]} value={values.gender} onChange={(v) => set("gender", v)} name="gender" />
      <Text label="الجنسية" value={values.nationality} onChange={(v) => set("nationality", v)} testid="q-nationality" />
      <div>
        <Select label="المنطقة" options={REGIONS} value={values.region_choice || (REGIONS.includes(values.region) ? values.region : (values.region ? "أخرى" : ""))} onChange={(v) => {
          if (v === "أخرى") { set("region_choice", "أخرى"); set("region", values.region_other || ""); }
          else { set("region_choice", v); set("region", v); set("region_other", ""); }
        }} testid="q-region" />
        {values.region_choice === "أخرى" && (
          <input
            autoFocus
            value={values.region_other || ""}
            onChange={(e) => { set("region_other", e.target.value); set("region", e.target.value); }}
            placeholder="اذكر اسم المنطقة"
            className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
            data-testid="q-region-other"
          />
        )}
      </div>
      <Text label="المدينة" value={values.city} onChange={(v) => set("city", v)} testid="q-city" />
      <Select label="العمر" options={AGES} value={values.age_range} onChange={(v) => set("age_range", v)} testid="q-age" />
      <Text label="رقم الجوال" dir="ltr" value={values.phone} onChange={(v) => set("phone", v)} testid="q-phone" />
      <Text label="البريد الإلكتروني" dir="ltr" type="email" value={values.email} onChange={(v) => set("email", v)} testid="q-email" />
    </div>
  );
}

function StepAcademic({ values, set }) {
  return (
    <div className="grid gap-4">
      <Select label="الجامعة" options={UNIVERSITIES} value={values.university} onChange={(v) => set("university", v)} testid="q-university" />
      {values.university === "أخرى" && (
        <Text label="اذكر اسم الجامعة" value={values.university_other} onChange={(v) => set("university_other", v)} testid="q-university-other" />
      )}
      <Text label="التخصص الدقيق" value={values.exact_specialization} onChange={(v) => set("exact_specialization", v)} placeholder="مثال: هندسة البرمجيات، المحاسبة، علم النفس الإكلينيكي" testid="q-exact-specialization" />
      <Select label="المجال الأكاديمي" options={ACADEMIC_FIELDS} value={values.academic_field} onChange={(v) => set("academic_field", v)} testid="q-academic-field" />
      <RadioGroup label="المستوى الدراسي" options={ACADEMIC_LEVELS} value={values.academic_level} onChange={(v) => set("academic_level", v)} name="level" />
    </div>
  );
}

function StepExperience({ values, set }) {
  return (
    <div className="grid gap-4">
      <RadioGroup label="هل سبق لك الالتحاق بتدريب تعاوني/تطوعي؟" options={["نعم","لا"]} value={values.has_experience} onChange={(v) => set("has_experience", v)} name="exp" />
      {values.has_experience === "نعم" && (
        <>
          <RadioGroup label="نوع التجربة" options={EXP_TYPES} value={values.experience_type} onChange={(v) => set("experience_type", v)} name="exp_type" />
          <Rating label="ما تقييمك للتجربة؟" min={1} max={5} value={values.experience_rating} onChange={(v) => set("experience_rating", v)} testid="q-exp-rating" />
          <Text label="أبرز الإيجابيات" value={values.experience_positives} onChange={(v) => set("experience_positives", v)} multiline testid="q-exp-pos" />
          <Text label="أبرز التحديات" value={values.experience_challenges} onChange={(v) => set("experience_challenges", v)} multiline testid="q-exp-chal" />
        </>
      )}
      {values.has_experience === "لا" && (
        <>
          <RadioGroup label="ما سبب عدم المشاركة؟" options={NO_EXP_REASONS} value={values.no_experience_reason} onChange={(v) => set("no_experience_reason", v)} name="no_exp" />
          {values.no_experience_reason === "أخرى" && (
            <Text label="اذكر السبب" value={values.no_experience_reason_other} onChange={(v) => set("no_experience_reason_other", v)} testid="q-no-exp-other" />
          )}
        </>
      )}
    </div>
  );
}

function StepExpectations({ values, set }) {
  return (
    <div className="grid gap-4">
      <RadioGroup label="هل لديك اهتمام بالعمل في الجمعيات الأهلية؟" options={INTEREST_NGO} value={values.interest_ngos} onChange={(v) => set("interest_ngos", v)} name="int_ngo" />
      <Rating label="مدى اهتمامك بالالتزام ببرنامج تدريب منظّم" min={1} max={5} value={values.commitment_level} onChange={(v) => set("commitment_level", v)} testid="q-commitment" />
      <CheckboxGroup label="أهدافك من المشاركة (يمكن اختيار عدة خيارات)" options={GOALS} value={values.goals || []} onChange={(v) => set("goals", v)} name="goals" />
      {values.goals?.includes("أخرى") && (
        <Text label="اذكر هدفاً آخر" value={values.goals_other} onChange={(v) => set("goals_other", v)} testid="q-goals-other" />
      )}
      <RadioGroup label="المدة المناسبة للتدريب" options={DURATIONS} value={values.preferred_duration} onChange={(v) => set("preferred_duration", v)} name="dur" />
      {values.preferred_duration === "أخرى" && (
        <Text label="اذكر المدة" value={values.preferred_duration_other} onChange={(v) => set("preferred_duration_other", v)} testid="q-duration-other" />
      )}
      <RadioGroup label="نمط التدريب المفضل" options={MODES} value={values.preferred_mode} onChange={(v) => set("preferred_mode", v)} name="mode" />
    </div>
  );
}

function StepSkills({ values, set }) {
  return (
    <div className="grid gap-3">
      <p className="text-sm text-slate-600 mb-2">قيّم مهاراتك من 1 (ضعيف جداً) إلى 5 (ممتاز).</p>
      {SKILLS.map((s) => (
        <Rating key={s.k} label={s.label} min={1} max={5} value={values[s.k]} onChange={(v) => set(s.k, v)} testid={`q-${s.k}`} />
      ))}
    </div>
  );
}

function StepDone({ user }) {
  return (
    <div className="text-center py-8">
      <div className="mx-auto h-16 w-16 rounded-full bg-teal-100 grid place-items-center mb-4">
        <PartyPopper className="h-9 w-9 text-teal-700" />
      </div>
      <h2 className="text-2xl font-heading font-black text-slate-900">تم! جاهزون لك 🎉</h2>
      <p className="mt-2 text-slate-600">ننقلك الآن إلى لوحة تحكمك...</p>
    </div>
  );
}

// -------- Field components --------
function Text({ label, value, onChange, type = "text", dir, multiline, testid }) {
  const Comp = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <Comp
        type={type} rows={multiline ? 3 : undefined} dir={dir}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
        data-testid={testid}
      />
    </label>
  );
}
function Select({ label, options, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid}>
        <option value="">— اختر —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function RadioGroup({ label, options, value, onChange, name }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o} type="button"
            onClick={() => onChange(o)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${value === o ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`}
            data-testid={`q-${name}-${o}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
function CheckboxGroup({ label, options, value, onChange, name }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button key={o} type="button" onClick={() => toggle(o)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${active ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`} data-testid={`q-${name}-${o}`}>
              {active && <Check className="inline h-3.5 w-3.5 ms-1" />}{o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function Rating({ label, min = 1, max = 5, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <div className="text-sm font-medium text-slate-700 flex-1">{label}</div>
      <div className="flex items-center gap-1.5" data-testid={testid}>
        {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((n) => (
          <button
            key={n} type="button" onClick={() => onChange(n)}
            className={`h-9 w-9 rounded-full text-xs font-bold border transition-all arabic-numerals ${value === n ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-500 border-slate-200 hover:border-amber-400"}`}
            data-testid={`${testid}-${n}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
